import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import request from '../utils/request';

function OrderDetailsScreen() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  // Read token from URL once (e.g. when user opens link from email); all API calls use Authorization header only
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [entryInstructions, setEntryInstructions] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');

  // Delivery time options (8:00 AM - 4:00 PM, 30-min intervals)
  const deliveryTimeOptions = [
    '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
    '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM'
  ];

  useEffect(() => {
    if (!token) {
      setError('Authentication token is missing. Please use the link from your email.');
      setLoading(false);
      return;
    }
    fetchOrderDetails();
  }, [orderId, token]);

  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchOrderDetails = async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const response = await request.get(`/orders/${orderId}/entry-instructions`, {
        headers: authHeader,
      });
      
      if (response.data.success) {
        setOrderData(response.data);
        // Do not prefill entry instructions or delivery time; show empty fields
        setEntryInstructions('');
        setDeliveryTime('');
      } else {
        setError(response.data.message || 'Failed to load order details');
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError(
        err.response?.data?.message || 
        'Failed to load order details. The link may have expired or is invalid.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!entryInstructions.trim()) {
      setError('Entry instructions are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await request.post(
        `/orders/${orderId}/entry-instructions`,
        {
          entryInstructions: entryInstructions.trim(),
          deliveryTime: deliveryTime || undefined,
        },
        { headers: authHeader }
      );

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          window.close(); // Close the window/tab if opened from email
        }, 3000);
      } else {
        setError(response.data.message || 'Failed to submit entry instructions');
      }
    } catch (err) {
      console.error('Error submitting entry instructions:', err);
      setError(
        err.response?.data?.message || 
        'Failed to submit entry instructions. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const pageBgStyle = {
    background:
      "linear-gradient(180deg, #FFFFFF 0%, #FFF5F4 30%, #F8FAFC 60%, #EFF4FF 100%)",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  };

  const renderShell = (content) => (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={pageBgStyle}
    >
      <div className="max-w-xl w-full">
        {content}
      </div>
    </div>
  );

  if (loading) {
    return renderShell(
      <div className="bg-white/90 border border-white/60 rounded-2xl shadow-xl p-10 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF4D4D] mx-auto mb-4"></div>
        <p className="text-gray-700 font-medium">Loading order details...</p>
      </div>
    );
  }

  if (error && !orderData) {
    return renderShell(
      <div className="bg-white/95 border border-white/60 rounded-2xl shadow-2xl p-8 text-center">
        <div className="text-red-500 mb-4">
          <svg className="w-14 h-14 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button
          onClick={() => window.close()}
          className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-full hover:bg-gray-300 transition"
        >
          Close
        </button>
      </div>
    );
  }

  if (success) {
    return renderShell(
      <div className="bg-white/95 border border-white/60 rounded-2xl shadow-2xl p-8 text-center">
        <div className="text-green-600 mb-4">
          <svg className="w-14 h-14 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Success!</h2>
        <p className="text-gray-600 mb-2">
          Your entry instructions have been submitted.
        </p>
        <p className="text-sm text-gray-500">
          This window will close automatically...
        </p>
      </div>
    );
  }

  return renderShell(
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/95 border border-white/60 rounded-2xl shadow-2xl p-8">
        <div className="flex items-start justify-between mb-6">
          <div className="space-y-2">
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-[#FFC1074D] bg-gradient-to-r from-[#FFF7E6] to-[#FFEBD2] shadow-sm">
              <span className="text-[11px] font-semibold tracking-[0.08em] text-[#FF9900] uppercase">Action Required</span>
            </div>
            <h1 className="text-[26px] leading-tight font-bold text-gray-900">Entry Instructions Needed</h1>
            <p className="text-[15px] text-gray-700">
              Please provide entry instructions and preferred delivery time for this order.
            </p>
          </div>
          <img
            src="https://bellhopt-public.s3.us-west-1.amazonaws.com/BellhoptLogoMedRes.png"
            alt="Bellhopt"
            className="h-12 w-12 object-contain ml-4 mt-1"
          />
        </div>
      </div>

      {/* Order Details Card */}
      {orderData && (() => {
        const order = orderData.order;
        return (
          <div className="bg-white/95 border border-white/60 rounded-2xl shadow-xl p-8">
            <h2 className="text-[17px] font-semibold text-gray-900 mb-4">Order Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <p className="text-[12px] text-gray-500 uppercase tracking-wide">Order ID</p>
                <p className="text-[15px] font-semibold text-gray-900">#{order.uniqueId ?? '—'}</p>
              </div>
              <div>
                <p className="text-[12px] text-gray-500 uppercase tracking-wide">Guest Email</p>
                <p className="text-[15px] font-semibold text-gray-900 break-all">{order.guestEmail}</p>
              </div>
              <div>
                <p className="text-[12px] text-gray-500 uppercase tracking-wide">Delivery Date</p>
                <p className="text-[15px] font-semibold text-gray-900">
                  {formatDate(order.inStockDate)}
                </p>
              </div>
              <div>
                <p className="text-[12px] text-gray-500 uppercase tracking-wide">Delivery Address</p>
                <p className="text-[15px] font-semibold text-gray-900 leading-snug">
                  {orderData.property.address.line1}
                  {orderData.property.address.line2 && (
                    <><br />{orderData.property.address.line2}</>
                  )}
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white/95 border border-white/60 rounded-2xl shadow-2xl p-8 space-y-6">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div>
          <label htmlFor="entryInstructions" className="block text-[14px] font-semibold text-gray-800 mb-2">
            Entry Instructions <span className="text-red-500">*</span>
          </label>
          <textarea
            id="entryInstructions"
            value={entryInstructions}
            onChange={(e) => setEntryInstructions(e.target.value)}
            rows={6}
            required
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6B6B] focus:border-[#FF6B6B] resize-y bg-white"
            placeholder="e.g., Keypad code is 1234, park in spot #7, enter through side door..."
          />
          <p className="mt-1 text-sm text-gray-500">
            This will update both the order entry instructions and property description.
          </p>
        </div>

        <div>
          <label htmlFor="deliveryTime" className="block text-[14px] font-semibold text-gray-800 mb-2">
            Delivery Time (when house cleaned)
          </label>
          <select
            id="deliveryTime"
            value={deliveryTime}
            onChange={(e) => setDeliveryTime(e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6B6B] focus:border-[#FF6B6B] bg-white"
          >
            <option value="">Select a time</option>
            {deliveryTimeOptions.map((time) => (
              <option key={time} value={time}>
                {time}
              </option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500">
            Preferred time for delivery when the house is cleaned.
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting || !entryInstructions.trim()}
            className="px-6 py-3 bg-gradient-to-r from-[#FF4D4D] to-[#FF6B6B] text-white font-semibold rounded-full shadow-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default OrderDetailsScreen;

